import { Spinner } from "@/components/ui/spinner"
import {
  contentControllerGetItemById,
  contentControllerGetManifestForProcessingContent,
  ContentItemCreationStatus,
  ContentItemViewMode,
} from "@/shared/api/generated"
import { QUERY_KEYS, ROUTER_PATHS } from "@/shared/constants"
import { MarkdownViewer, PdfViewer, TextViewer } from "@/widgets"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { queryClient } from "@/shared/api/query-client.ts"
import { useProcessSocketStore } from "@/entities"

const LibraryRender = ({ id }: { id: string }) => {
  const { data } = useQuery({
    queryKey: QUERY_KEYS.CONTENT_BY_ID(id),
    queryFn: () => contentControllerGetItemById(id),
    enabled: false,
    staleTime: Infinity,
    initialData: () => {
      return queryClient.getQueryData(QUERY_KEYS.CONTENT_BY_ID(id))
    },
  })

  if (!data) return null

  if (data.viewMode === ContentItemViewMode.MARKDOWN) {
    return <MarkdownViewer itemData={data} />
  }

  // SELECTION items (browser extension): plain text + per-chunk word timings
  if (data.viewMode === ContentItemViewMode.TEXT) {
    return <TextViewer itemData={data} />
  }

  return <PdfViewer itemData={data} />
}

export const LibraryDetailsPage = () => {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    const handler = async () => {
      const res = await contentControllerGetItemById(id)

      if (
        res.creationStatus ===
        ContentItemCreationStatus.PARTIALLY_COMPLETED_AND_STILL_PROCESSING
      ) {
        // Opening an existing still-processing note doesn't guarantee the
        // /process socket is connected - it only connects right after
        // creating content, or on app load (and only then if the server
        // says a task is active). Without this, CONTENT_NEW_CHUNK_COMPLETED
        // never reaches us while listening, so the chunk engine's own
        // stall recovery is the only chance to notice new chunks.
        void useProcessSocketStore.getState().connectToProcessSocket()
        queryClient.setQueryData(
          QUERY_KEYS.CONTENT_MANIFEST_BY_ID(id),
          await contentControllerGetManifestForProcessingContent(id)
        )
      }

      queryClient.setQueryData(QUERY_KEYS.CONTENT_BY_ID(id), res)
      setIsLoading(false)
    }

    handler()
  }, [id])

  if (!id) {
    return <Navigate to={ROUTER_PATHS.APP_LIBRARY} replace />
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="h-full w-full">
      <LibraryRender id={id} />
    </div>
  )
}
